import type { ReadingSlide } from "../ui/StartReader";
import { AppChip, C, Frame, MONO } from "../ui/readingArt";
import { tx, txv } from "../lib/tx";

/**
 * "Read while you wait" summary for the passwords-mfa start screen. Three short
 * slides (what/who and why the learner is the control, main risks, key terms)
 * shown in the reader while the 3D room downloads. Content only; the reader UI
 * lives in ui/StartReader.tsx.
 */

/* ------------------------------------------------------------------ */
/* 1. one leaked password, many accounts                               */
/* ------------------------------------------------------------------ */
interface TargetRow {
  y: number;
  name: string;
  sub: string;
  status: string;
  ok: boolean;
  pillX: number;
  pillW: number;
}

const TARGETS: TargetRow[] = [
  { y: 118, name: "Work email", sub: "tries Summer2026!", status: "Opens", ok: false, pillX: 270, pillW: 62 },
  { y: 164, name: "Shared drive", sub: "tries Summer2026!", status: "Opens", ok: false, pillX: 270, pillW: 62 },
  { y: 210, name: "Bank portal", sub: "its own random password", status: "Stays shut", ok: true, pillX: 248, pillW: 84 },
];

function ReuseArt(): JSX.Element {
  const H = 296;
  return (
    <Frame label={tx("Example: one leaked password, many accounts")} aria={tx("A password leaked from a streaming site is tried on work email and a shared drive, where it opens them, but not on a bank portal that has its own password")} height={H}>
      <rect x="16.5" y="34.5" width="327" height="54" rx="9" fill={C.panel2} stroke={C.line} />
      <circle cx="40" cy="61" r="12" fill={C.mint} fillOpacity="0.14" />
      <path d="M37 55.5 L46 61 L37 66.5 Z" fill={C.mint} />
      <text x="62" y="58" fontSize="13" fontWeight="700" fill={C.text}>
        {tx("Streaming site")}
      </text>
      <text x="62" y="74" fontSize="10.5" fill={C.muted}>
        {tx("Leaked in a breach")}
      </text>
      <rect x="196.5" y="46.5" width="136" height="30" rx="8" fill={C.code} stroke={C.red} strokeOpacity="0.5" />
      <circle className="sr-anim-ring" cx="210" cy="61.5" r="4.5" fill="none" stroke={C.red} strokeWidth="1.4" />
      <circle cx="210" cy="61.5" r="2.2" fill={C.red} />
      <text x="222" y="65.5" fontSize="12" fontFamily={MONO} fill={C.red}>
        {tx("Summer2025!")}
      </text>

      <line x1="26" y1="89" x2="26" y2="230" stroke={C.line} strokeWidth="1.5" />
      {TARGETS.map((t) => {
        const col = t.ok ? C.mint : C.red;
        return (
          <g key={t.name}>
            <line x1="26" y1={t.y + 20} x2="40" y2={t.y + 20} stroke={C.line} strokeWidth="1.5" />
            <rect x="40.5" y={t.y + 0.5} width="303" height="40" rx="9" fill={C.panel2} stroke={C.line} />
            <text x="54" y={t.y + 17} fontSize="12.5" fontWeight="700" fill={C.text}>
              {txv(t.name)}
            </text>
            <text x="54" y={t.y + 31} fontSize="10.5" fontFamily={MONO} fill={C.muted}>
              {txv(t.sub)}
            </text>
            <rect
              className={t.name === "Work email" ? "sr-anim-pulse" : undefined}
              x={t.pillX}
              y={t.y + 9}
              width={t.pillW}
              height="22"
              rx="11"
              fill={col}
              fillOpacity="0.14"
              stroke={col}
              strokeOpacity="0.5"
            />
            <text x={t.pillX + t.pillW / 2} y={t.y + 24} fontSize="11" fontWeight="700" fill={col} textAnchor="middle">
              {txv(t.status)}
            </text>
          </g>
        );
      })}

      <text x="180" y="276" fontSize="11.5" fill={C.muted} textAnchor="middle">
        {tx("Each account needs its own password.")}
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. fake sign-in page + public facts                                 */
/* ------------------------------------------------------------------ */
function FakePageArt(): JSX.Element {
  const H = 296;
  return (
    <Frame label={tx("Example: a sign-in page you were sent")} aria={tx("A fake company sign-in page with an odd web address and a password being typed, and below it a dog name and birth year posted publicly that make up an easy password")} height={H}>
      <rect x="16.5" y="34.5" width="327" height="140" rx="9" fill={C.code} stroke={C.line} />
      <rect x="26" y="42" width="308" height="22" rx="11" fill={C.panel2} stroke={C.line} />
      <circle className="sr-anim-ring" cx="40" cy="53" r="4.5" fill="none" stroke={C.red} strokeWidth="1.4" />
      <circle cx="40" cy="53" r="2.2" fill={C.red} />
      <text x="52" y="57" fontSize="10.5" fontFamily={MONO} fill={C.amber}>
        signin.company-login.co/verify
      </text>

      <text x="32" y="86" fontSize="12.5" fontWeight="700" fill={C.text}>
        {tx("Sign in to your account")}
      </text>
      <rect x="32.5" y="94.5" width="295" height="22" rx="5" fill={C.panel2} stroke={C.line} />
      <text x="42" y="109.5" fontSize="11.5" fill={C.soft}>
        {tx("alex.morgan")}
      </text>
      <rect x="32.5" y="122.5" width="295" height="22" rx="5" fill={C.panel2} stroke={C.mint} strokeOpacity="0.6" />
      <text x="42" y="138" fontSize="12" fontFamily={MONO} fill={C.soft} letterSpacing="2">
        {"\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"}
      </text>
      <rect className="sr-anim-blink" x="146" y="127" width="1.5" height="13" rx="0.75" fill={C.mint} />
      <rect x="32" y="151" width="76" height="18" rx="5" fill={C.mint} fillOpacity="0.16" stroke={C.mint} strokeOpacity="0.5" />
      <text x="70" y="163.5" fontSize="11" fontWeight="700" fill={C.mint} textAnchor="middle">
        {tx("Sign in")}
      </text>

      <rect x="16.5" y="186.5" width="327" height="92" rx="8" fill={C.amber} fillOpacity="0.08" stroke={C.amber} strokeOpacity="0.4" />
      <text x="30" y="206" fontSize="12" fontWeight="700" fill={C.amber}>
        {tx("Posted on a public profile")}
      </text>
      <rect x="30.5" y="215.5" width="100" height="22" rx="11" fill={C.panel2} stroke={C.line} />
      <text x="80.5" y="230.5" fontSize="11" fill={C.soft} textAnchor="middle">
        {tx("Dog: Biscuit")}
      </text>
      <rect x="140.5" y="215.5" width="90" height="22" rx="11" fill={C.panel2} stroke={C.line} />
      <text x="185.5" y="230.5" fontSize="11" fill={C.soft} textAnchor="middle">
        {tx("Born 2004")}
      </text>
      <text x="30" y="266" fontSize="11" fill={C.muted}>
        {tx("Easy to work out:")}
      </text>
      <text x="124" y="266" fontSize="12.5" fontWeight="700" fontFamily={MONO} fill={C.red}>
        {tx("Biscuit2004")}
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. passphrase vs weak password + MFA approve prompt                 */
/* ------------------------------------------------------------------ */
function StrengthRow({ y, text, filled, color, tag }: { y: number; text: string; filled: number; color: string; tag: string }): JSX.Element {
  return (
    <g>
      <text x="30" y={y} fontSize="12" fontFamily={MONO} fill={C.text}>
        {txv(text)}
      </text>
      <text x="330" y={y} fontSize="10.5" fontWeight="700" fill={color} textAnchor="end">
        {txv(tag)}
      </text>
      {[0, 1, 2, 3].map((i) => (
        <rect key={i} x={30 + i * 74} y={y + 8} width="70" height="6" rx="3" fill={i < filled ? color : C.line} fillOpacity={i < filled ? 0.9 : 1} />
      ))}
    </g>
  );
}

function SignInArt(): JSX.Element {
  const H = 292;
  return (
    <Frame label={tx("Example: stronger sign-in")} aria={tx("A short pattern password shown as weak next to four random words shown as strong, and a sign-in approval prompt with a pulsing Approve button")} height={H}>
      <rect x="16.5" y="34.5" width="327" height="100" rx="9" fill={C.panel2} stroke={C.line} />
      <StrengthRow y={58} text={tx("Summer2026!")} filled={1} color={C.red} tag={tx("Easy to guess")} />
      <StrengthRow y={100} text={tx("plum tractor violin sand")} filled={4} color={C.mint} tag={tx("Hard to guess")} />

      <rect x="16.5" y="146.5" width="327" height="134" rx="10" fill={C.panel2} stroke={C.line} />
      <AppChip x={30} y={158} />
      <text x="62" y="175" fontSize="13" fontWeight="700" fill={C.text}>
        {tx("Approve sign-in?")}
      </text>
      <rect x="276" y="160" width="54" height="20" rx="10" fill={C.amber} fillOpacity="0.12" stroke={C.amber} strokeOpacity="0.45" />
      <text x="303" y="174" fontSize="10.5" fontFamily={MONO} fontWeight="700" fill={C.amber} textAnchor="middle">
        02:14
      </text>
      <text x="30" y="205" fontSize="10.5" fill={C.muted}>
        {tx("Request from an unknown location")}
      </text>
      <text x="30" y="222" fontSize="12" fill={C.soft}>
        {tx("Did you just try to sign in?")}
      </text>
      <rect x="30.5" y="234.5" width="138" height="32" rx="8" fill={C.panel} stroke={C.line} />
      <text x="99.5" y="254.5" fontSize="12" fontWeight="700" fill={C.soft} textAnchor="middle">
        {tx("Deny")}
      </text>
      <rect className="sr-anim-ring" x="180.5" y="234.5" width="148" height="32" rx="8" fill="none" stroke={C.amber} strokeWidth="1.5" />
      <rect className="sr-anim-pulse" x="180.5" y="234.5" width="148" height="32" rx="8" fill={C.mint} fillOpacity="0.16" stroke={C.mint} strokeOpacity="0.55" />
      <text x="254.5" y="254.5" fontSize="12" fontWeight="700" fill={C.mint} textAnchor="middle">
        {tx("Approve")}
      </text>
    </Frame>
  );
}

export const PASSWORDS_MFA_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "One leaked password should not open every door.",
    illustration: ReuseArt,
    caption: "A leaked password gets tried everywhere.",
    sections: [
      {
        h: "What this is about",
        body: "A short briefing on passwords and sign-in. You watch a film, then play the attacker against one employee of a fictional bank. You see how a leaked password, a familiar pattern and a few public facts are enough to get in.",
      },
      {
        h: "Who it\u2019s for",
        body: "Everyone who signs in to anything at work. No technical background needed.",
      },
      {
        h: "Why your judgement matters",
        body: "Systems can lock accounts and ask for a second check, but they cannot stop you typing a password into a fake page, or reusing one you already used elsewhere. What you type, where you type it and what you approve are your choices. You are the control.",
      },
    ],
  },
  {
    tab: "Main risks",
    headline: "Small habits that let attackers in.",
    illustration: FakePageArt,
    caption: "A strong password still fails on a fake page.",
    sections: [
      {
        h: "Reused and patterned passwords",
        body: "When one site leaks a password, attackers try it on others, and on the same pattern with a newer year. Changing one digit does not make a new secret.",
      },
      {
        h: "Facts anyone can find",
        body: "A pet\u2019s name, a birth year or a team you support often sits on a public profile. A password built from them is easy to work out.",
      },
      {
        h: "Fake sign-in pages",
        body: "A link in an email or message can lead to a page that looks like your company\u2019s. The password is lost the moment you type it in, however strong it is. Open the real service from a bookmark or the app instead.",
      },
      {
        h: "Approval prompts you did not start",
        body: "If you did not just sign in, an approval request means someone has your password. Deny it, change the password and report it. Never approve to make it stop.",
      },
    ],
  },
  {
    tab: "Key terms",
    headline: "The words you\u2019ll meet today.",
    illustration: SignInArt,
    caption: "Long and unique beats clever and short.",
    sections: [
      { h: "Leaked password", body: "A password that has been exposed in a breach. Treat it as public, and never use it again anywhere." },
      { h: "Password reuse", body: "Using the same password, or a small variation, on more than one account. One leak then opens them all." },
      { h: "Passphrase", body: "A long password made of several unrelated words. It is easier to remember and much harder to guess." },
      { h: "Password manager", body: "A tool that creates and stores a different strong password for every account, so you remember only one." },
      { h: "Multi-factor authentication (MFA)", body: "A second check after the password, such as an approval on your phone. It helps only if you approve requests that are really yours." },
      { h: "Fake sign-in page", body: "A copy of a real login page built to collect what you type. The address is often slightly off." },
    ],
  },
];
